import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import HtmlEditor from "../../components/admin/HtmlEditor";
import ImageUpload from "../../components/admin/ImageUpload";

const blank = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "Reptile Global",
  tags: [],
  seo: { title: "", description: "", keywords: "", og_image: "", og_type: "article", og_url: "", twitter_card: "summary_large_image", canonical_url: "", json_ld: "" },
  is_published: true,
};

const inputCls = "w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 text-sm";
const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
    {children}
    {hint && <div className="text-xs text-zinc-400 mt-1">{hint}</div>}
  </div>
);

const AdminBlogEditor = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/blog/${id}`).then((r) => setForm({ ...blank, ...r.data, seo: { ...blank.seo, ...(r.data.seo || {}) } })).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...f.seo, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
      if (isNew) {
        const { data } = await api.post("/admin/blog", payload);
        toast.success("Created");
        navigate(`/admin/blog/${data.id}`);
      } else {
        await api.put(`/admin/blog/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="admin-blog-editor">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-black tracking-tight">{isNew ? "New post" : `Edit: ${form.title}`}</h1>
        <button type="submit" disabled={saving} data-testid="admin-blog-save" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Title *"><input className={inputCls} required value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="blog-title" /></Field>
        <Field label="Slug" hint="URL path (auto-generated if blank)"><input className={inputCls} value={form.slug} onChange={(e) => upd("slug", e.target.value)} data-testid="blog-slug" /></Field>
        <Field label="Author"><input className={inputCls} value={form.author} onChange={(e) => upd("author", e.target.value)} data-testid="blog-author" /></Field>
        <Field label="Tags (comma separated)">
          <input className={inputCls} value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => upd("tags", e.target.value)} data-testid="blog-tags" />
        </Field>
        <div className="md:col-span-2">
          <ImageUpload label="Cover image" value={form.cover_image} onChange={(v) => upd("cover_image", v)} testId="blog-cover-upload" />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <input id="bpub" type="checkbox" checked={form.is_published} onChange={(e) => upd("is_published", e.target.checked)} data-testid="blog-published" />
          <label htmlFor="bpub" className="text-sm font-semibold">Published</label>
        </div>
        <div className="md:col-span-2"><Field label="Excerpt"><textarea className={`${inputCls} min-h-[80px]`} value={form.excerpt} onChange={(e) => upd("excerpt", e.target.value)} data-testid="blog-excerpt" /></Field></div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Content (HTML editor)</label>
          <HtmlEditor value={form.content} onChange={(v) => upd("content", v)} minHeight={360} testId="blog-content" />
        </div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">SEO Meta</div>
        <Field label="Meta title"><input className={inputCls} value={form.seo.title} onChange={(e) => updSeo("title", e.target.value)} /></Field>
        <Field label="Keywords"><input className={inputCls} value={form.seo.keywords} onChange={(e) => updSeo("keywords", e.target.value)} /></Field>
        <div className="md:col-span-2"><Field label="Meta description"><textarea className={`${inputCls} min-h-[80px]`} value={form.seo.description} onChange={(e) => updSeo("description", e.target.value)} /></Field></div>
        <div className="md:col-span-2"><ImageUpload label="OG image" value={form.seo.og_image} onChange={(v) => updSeo("og_image", v)} testId="blog-og-upload" /></div>
        <Field label="OG type" hint="article, website"><input className={inputCls} value={form.seo.og_type} onChange={(e) => updSeo("og_type", e.target.value)} placeholder="article" /></Field>
        <Field label="OG URL"><input className={inputCls} value={form.seo.og_url} onChange={(e) => updSeo("og_url", e.target.value)} /></Field>
        <Field label="Twitter card" hint="summary or summary_large_image"><input className={inputCls} value={form.seo.twitter_card} onChange={(e) => updSeo("twitter_card", e.target.value)} placeholder="summary_large_image" /></Field>
        <Field label="Canonical URL"><input className={inputCls} value={form.seo.canonical_url} onChange={(e) => updSeo("canonical_url", e.target.value)} /></Field>
        <div className="md:col-span-2">
          <Field label="JSON-LD Schema" hint="Article schema. Paste a single JSON object.">
            <textarea className={`${inputCls} min-h-[140px] font-mono text-xs`} value={form.seo.json_ld} onChange={(e) => updSeo("json_ld", e.target.value)} placeholder='{"@context":"https://schema.org","@type":"Article", ...}' />
          </Field>
        </div>
      </section>
    </form>
  );
};

export default AdminBlogEditor;
